import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';
import ConfirmActionModal from '../../components/modals/ConfirmActionModal';
import '../../styles/Table.css';

const CategoriesListPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    fetchCategories();
  }, [searchTerm]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, 500);
  };

  const fetchCategories = async () => {
    try {
      const params = {};
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const res = await api.getCategories(params);
      if (res.success) {
        const activeElement = document.activeElement;
        const wasSearchFocused = activeElement === searchInputRef.current;
        setCategories(res.data || []);
        if (wasSearchFocused && searchInputRef.current) {
          setTimeout(() => searchInputRef.current?.focus(), 0);
        }
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Error fetching categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteCategory(id);
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Error deleting category');
    }
  };

  if (loading && categories.length === 0) {
    return <Loader />;
  }

  return (
    <div className="categories-list-page" style={{ padding: '16px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <div style={{ flex: 1, marginRight: '12px' }}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search categories by name..."
            defaultValue={searchTerm}
            onChange={handleSearchChange}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '2px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
            }}
          />
        </div>
        <button
          onClick={() => navigate('/admin/categories/add')}
          style={{
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span>+</span>
          <span>Add Category</span>
        </button>
      </div>

      <div
        style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        {categories.length === 0 && !loading ? (
          <div
            style={{
              padding: '40px',
              textAlign: 'center',
              color: '#718096',
              fontSize: '14px',
            }}
          >
            No categories found
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#718096',
                    }}
                  >
                    Category Name
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#718096',
                    }}
                  >
                    Description
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#718096',
                      width: '100px',
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#718096',
                      width: '120px',
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => {
                  const id = category._id || category.id;
                  return (
                    <tr key={id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px', color: '#2d3748', fontWeight: 600, fontSize: '13px' }}>
                        {category.name}
                      </td>
                      <td style={{ padding: '10px', color: '#4a5568', fontSize: '13px' }}>
                        {category.description || 'N/A'}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <span
                          style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            backgroundColor: category.isActive ? '#d1fae5' : '#fee2e2',
                            color: category.isActive ? '#065f46' : '#991b1b',
                          }}
                        >
                          {category.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button
                            onClick={() => navigate(`/admin/categories/${id}/edit`)}
                            style={{
                              padding: '6px 10px',
                              background: '#e6fffa',
                              color: '#234e52',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: 500,
                            }}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => setDeleteTarget(id)}
                            style={{
                              padding: '6px 10px',
                              background: '#fed7d7',
                              color: '#c53030',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: 500,
                            }}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmActionModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            handleDelete(deleteTarget);
            setDeleteTarget(null);
          }
        }}
        title="Delete Category"
        message="Are you sure you want to delete this category? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default CategoriesListPage;

